import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "Settled", label: "Settled (Actual Outflow)" },
    { value: "Pending", label: "Pending (Committed)" },
    { value: "Rejected", label: "Rejected" },
];

const CashFlowReport = () => {
    const [accounts, setAccounts] = useState([]);
    const [summary, setSummary] = useState({ Pending: 0, Settled: 0, Rejected: 0 });
    const [byAccount, setByAccount] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activepaymentaccounts.php')
            .then((res) => { if (mounted) setAccounts(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Cash Flow Report"
            subtitle="Money outflow by payment account with settled, committed and rejected totals"
            breadcrumb={["Reports", "Expenses"]}
            fetchUrl="/reports/expenses/cash_flow.php"
            rowKey="exppay_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 4, placeholder: "Pay ref, payment ref, exp ref" },
                { type: "select", key: "payacc_id", label: "Account", col: 4, options: accounts, showSearch: true },
                { type: "select", key: "status", label: "Status", col: 4, options: STATUS_OPTIONS },
                { type: "date", key: "dateRange", label: "Release Date", col: 6 },
            ]}
            onData={(dataset, apiData) => {
                setSummary(apiData?.summary || { Pending: 0, Settled: 0, Rejected: 0 });
                setByAccount(apiData?.by_account || []);
            }}
            summaryCards={[
                { label: "Actual Outflow (Settled)", value: fmtMoney(summary.Settled), color: "red" },
                { label: "Committed (Pending)", value: fmtMoney(summary.Pending), color: "orange" },
                { label: "Rejected", value: fmtMoney(summary.Rejected), color: "blue" },
            ]}
            columns={[
                { title: 'Date', dataIndex: 'payment_release_date', key: 'payment_release_date', width: 115, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.payment_release_date).valueOf() - dayjs(b.payment_release_date).valueOf() },
                { title: 'Pay Ref', dataIndex: 'exppay_ref', key: 'exppay_ref', width: 125 },
                { title: 'Expense Ref', dataIndex: 'exp_ref', key: 'exp_ref', width: 125, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Expense Title', dataIndex: 'exp_title', key: 'exp_title', render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Account', dataIndex: 'account_name', key: 'account_name', width: 150, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Type', dataIndex: 'account_type', key: 'account_type', width: 80, render: (v) => v || "—" },
                { title: 'Amount', dataIndex: 'paid_amount', key: 'paid_amount', align: 'right', width: 120, sorter: (a, b) => (a.paid_amount || 0) - (b.paid_amount || 0), render: (v) => fmtMoney(v) },
                {
                    title: 'Status', dataIndex: 'payment_status', key: 'payment_status', width: 110,
                    render: (s) => {
                        const color = s === "Settled" ? "#52c41a" : (s === "Rejected" ? "#ff4d4f" : "#faad14");
                        return <span style={{ color, fontWeight: 600 }}>{s}</span>;
                    },
                },
                { title: 'Checked By', dataIndex: 'checked_name', key: 'checked_name', width: 130, render: (v) => v || "—" },
            ]}
            pdf={{
                title: "CASH FLOW REPORT",
                subtitle: "Money outflow by payment account with settled, committed and rejected totals",
                columns: [
                    { header: "Date" }, { header: "Pay Ref" }, { header: "Expense Ref" }, { header: "Expense Title" },
                    { header: "Account" }, { header: "Type" }, { header: "Amount", align: "right" },
                    { header: "Status" }, { header: "Checked By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        dayjs(r.payment_release_date).format("YYYY-MM-DD"),
                        r.exppay_ref || "-",
                        r.exp_ref || "-",
                        r.exp_title || "-",
                        r.account_name || "-",
                        r.account_type || "-",
                        fmtMoney(r.paid_amount),
                        r.payment_status || "-",
                        r.checked_name || "-",
                    ]),
                summary: (rows, apiData) => {
                    const s = apiData?.summary || {};
                    const lines = [
                        { label: "Actual Outflow (Settled):", value: fmtMoney(s.Settled) },
                        { label: "Committed (Pending):", value: fmtMoney(s.Pending) },
                        { label: "Rejected:", value: fmtMoney(s.Rejected) },
                    ];
                    (apiData?.by_account || []).forEach((acc) => {
                        lines.push({ label: `${acc.account_name}:`, value: fmtMoney(acc.total) });
                    });
                    return lines;
                },
                totalRow: (rows, apiData) => [
                    "TOTAL", "", "", "", "", "", fmtMoney(apiData?.summary?.Settled || 0), "", "",
                ],
                columnStyles: { 6: { halign: "right" } },
            }}
            excel={{
                filename: "cash-flow-report",
                sheetName: "Cash Flow",
                columns: [
                    { title: "Date", dataIndex: "payment_release_date", width: 12 },
                    { title: "Pay Ref", dataIndex: "exppay_ref", width: 14 },
                    { title: "Expense Ref", dataIndex: "exp_ref", width: 14 },
                    { title: "Expense Title", dataIndex: "exp_title", width: 30 },
                    { title: "Account", dataIndex: "account_name", width: 18 },
                    { title: "Type", dataIndex: "account_type", width: 8 },
                    { title: "Amount", dataIndex: "paid_amount", width: 14 },
                    { title: "Status", dataIndex: "payment_status", width: 12 },
                    { title: "Checked By", dataIndex: "checked_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        paid_amount: fmtMoney(r.paid_amount),
                        payment_release_date: dayjs(r.payment_release_date).format("YYYY-MM-DD"),
                    })),
                extraRows: (rows, apiData) => {
                    const out = [];
                    (apiData?.by_account || []).forEach((acc) => {
                        out.push([`Settled - ${acc.account_name}`, "", "", "", "", "", fmtMoney(acc.total), "", ""]);
                    });
                    out.push(["TOTAL SETTLED (OUTFLOW)", "", "", "", "", "", fmtMoney(apiData?.summary?.Settled || 0), "", ""]);
                    out.push(["TOTAL COMMITTED (PENDING)", "", "", "", "", "", fmtMoney(apiData?.summary?.Pending || 0), "", ""]);
                    return out;
                },
            }}
        />
    );
};

export default CashFlowReport;
